import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Button, Loader } from 'ncoded-component-library';
import { useTranslation } from 'react-i18next';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import SideDrawer from '../../../SideDrawer';
import DataRoomMemberInfo from '../../../DataRoomMemberInfo';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import {
  DataRoomMember,
  DataRoomMembers,
} from 'router/subrouters/DataRoom/models/DataRoomMember';
import QaRolesCustomSelect from 'components/QaRolesCustomSelect';
import useOptions from 'router/subrouters/DataRoom/subrouters/Members/hooks/useOptions';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';
import MemberRoles from '../../../MemberRoles';
import ResolveMultiSelect from 'components/ResolveMultiSelect';
import { Link } from 'react-router-dom';
import MemberFileAccessSelector from '../../../MemberFileAccessSelector';
import LinkToIcon from 'icons/LinkTo.icon';
import { getDataRoomRoute } from 'utils';
import TrashIcon from 'icons/Trash.icon';
import PauseIcon from 'icons/Pause.icon';
import confirm from 'modules/confirm';
import { isDataRoomAdminOrOwner } from 'router/subrouters/DataRoom/utils';
import useRevokeFileAccess from 'router/subrouters/DataRoom/subrouters/Members/pages/Members/hooks/useRevokeFIleAccess';
import PopNotificationWithUndo from 'components/PopNotificationWithUndo';
import useCallbackAsync from 'hooks/useCallbackAsync';

import './DocumentsAvailabilityDrawer.styles.scss';

type DocumentsAvailabilityDrawerProps = {
  className?: string;
  member: DataRoomMember;
  isBuySide: boolean;
  defaultOpenRoles?: boolean;
  hasAdditionalActions?: boolean;
  updateMember?: (member: DataRoomMember) => void;
  updateDataRoomMembers?: (members: DataRoomMembers) => void;
};

const DocumentsAvailabilityDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  DocumentsAvailabilityDrawerProps
> = (
  {
    className,
    member,
    isBuySide,
    hasAdditionalActions,
    defaultOpenRoles = false,
    updateMember,
    updateDataRoomMembers,
  },
  ref,
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  const classes = classNames('documents-availability-drawer', className);

  const { t } = useTranslation();
  const { buyerRoleOptions, buyerQaRole, sellerRoleOptions, sellerQaRole } =
    useOptions();

  const {
    dataRoom: { _id: dataRoomId, teams },
  } = useContext(DataRoomContext);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const isAdmin = isDataRoomAdminOrOwner(member?.role);

  const dataRoomTeamsOptions = useMemo(() => {
    const array = Array.from(
      new Set([...(teams || []), ...(member?.teams || [])]),
    );

    return array.map((team) => ({
      value: team,
      label: team,
    }));
  }, [teams, member]);

  const onSubmit = useCallback(
    async (values: Partial<DataRoomMember>) => {
      try {
        setIsLoading(true);

        await dataRoomApi.members.updateMember(member?._id, values);

        updateMember?.({ ...member, ...values });
      } catch (e) {
        popServerError(e);
      } finally {
        setIsLoading(false);
      }
    },
    [member, popServerError, updateMember],
  );

  const remindColleague = useCallback(async () => {
    try {
      setIsLoading(true);
      await dataRoomApi.members.notifyBuySideMember(member?._id);
      popSuccess(t('successInvitedBuySideMember'));
    } catch (e) {
      popServerError(e);
    } finally {
      setIsLoading(false);
    }
  }, [member?._id, popServerError, popSuccess, t]);

  const handleOnRevokeAccess = useCallback(
    () => dataRoomApi.members.revokeAccess(member?._id),
    [member],
  );

  const handleOnUndoRevokeAccess = useCallback(
    () => dataRoomApi.members.undoRevokeAccess(member?._id),
    [member],
  );

  const updateValueOnRevokeSuccess = useCallback(
    (member: DataRoomMember) =>
      updateMember({
        ...member,
        accessableDataRoomNodesView: [],
      }),
    [updateMember],
  );

  const {
    isLoadingRevoke,
    isUndoAvailable,
    refetchFlag,
    hasFileAccess,
    onRevokeAccess,
    onUndoRevokeAccess,
    refetch,
  } = useRevokeFileAccess({
    value: member,
    numberOfAccess: member?.accessableDataRoomNodesView?.length,
    handleOnRevokeAccess,
    handleOnUndoRevokeAccess,
    updateValueOnRevokeSuccess,
    updateValueOnUndoSuccess: updateMember,
  });

  const handleRemoveMember = useCallback(async () => {
    if (
      await confirm(
        {
          title: t('removeMember'),
          content: t('removeMemberMessage'),
        },
        true,
      )
    ) {
      try {
        setIsLoading(true);
        const { data } = await dataRoomApi.members.removeMember(member?._id);
        updateDataRoomMembers?.(data);
        drawer?.setOpen(false);
      } catch (error) {
        popServerError(error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [member, drawer, updateDataRoomMembers, popServerError, t]);

  const [onHasFullAccessChange, isLoadingHasFullAccess] = useCallbackAsync(
    async (hasFullAccess: boolean) => {
      const { data } = await dataRoomApi.members[
        hasFullAccess
          ? 'grantFilesAccessWithoutUndo'
          : 'revokeFilesAccessWithoutUndo'
      ](member?._id);

      updateMember?.(data);
      refetch();
    },
  );

  return (
    <>
      <SideDrawer ref={drawerRef} className={classes}>
        <DataRoomMemberInfo
          className="white-panel"
          member={member}
          isBuySide={isBuySide}
          remindColleague={remindColleague}
        />

        {drawer?.open && !isAdmin && (
          <MemberFileAccessSelector
            showSelectAllCheckbox
            hasFullAccess={member?.hasFullAccess}
            dataRoomId={dataRoomId}
            refetchFlag={refetchFlag}
            isDisabled={isUndoAvailable || isLoadingHasFullAccess}
            dataRoomMemberId={member?._id}
            isLoadingHasFullAccess={isLoadingHasFullAccess}
            onHasFullAccessChange={onHasFullAccessChange}
            updateMember={updateMember}
          />
        )}

        <MemberRoles defaultOpen={defaultOpenRoles}>
          <QaRolesCustomSelect
            name="role"
            label={t('documentsRole')}
            options={isBuySide ? buyerRoleOptions : sellerRoleOptions}
            renderAsPortal
            value={member?.role}
            onChange={onSubmit}
          />

          <QaRolesCustomSelect
            name="qaRole"
            label={t('qaRole')}
            options={isBuySide ? buyerQaRole : sellerQaRole}
            renderAsPortal
            value={member?.qaRole}
            onChange={onSubmit}
          />

          <div className="column">
            <label>{t('assignTeamRole')}</label>

            <ResolveMultiSelect
              options={dataRoomTeamsOptions}
              value={member?.teams || []}
              disabled={!dataRoomTeamsOptions?.length}
              onChange={(value) => onSubmit({ teams: value })}
              renderAsPortal
            />
          </div>

          <span className="manage-in-settings">
            {t('manageTeamRoles')}
            <Link to={getDataRoomRoute(dataRoomId, 'settings')}>
              {t('inYourSettings')}
              <LinkToIcon />
            </Link>
          </span>

          {hasAdditionalActions && !isAdmin && (
            <div className="actions">
              {hasFileAccess && (
                <Button
                  onClick={onRevokeAccess}
                  icon={PauseIcon}
                  disabled={isLoadingRevoke}
                  variant="link"
                >
                  {t('revokeAllFileAccess')}
                </Button>
              )}

              <Button
                variant="link"
                icon={TrashIcon}
                onClick={handleRemoveMember}
                className="warning-action-button"
              >
                {t('deleteMember')}
              </Button>
            </div>
          )}

          {isLoading && <Loader />}
        </MemberRoles>
      </SideDrawer>

      <PopNotificationWithUndo
        isUndoAvailable={isUndoAvailable}
        disabled={isLoadingRevoke}
        onUndo={onUndoRevokeAccess}
      >
        <p>{t('fileAccessRevokedMember')}</p>
      </PopNotificationWithUndo>
    </>
  );
};

export default forwardRef(DocumentsAvailabilityDrawer);
